import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Segmented } from '../../components/ui';
import { Copy, Download } from '../../components/icons';
import { drawChartPNG, slug, writtenPattern } from '../../domain/export';
import type { EditorApi } from './useEditor';
import type { PatternRecord } from '../../domain/types';
import { makeBundle } from '../../store/model';

type Tab = 'json' | 'written' | 'png';

function save(name: string, text: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function ExportModal({ open, onClose, editor, record }: { open: boolean; onClose: () => void; editor: EditorApi; record: PatternRecord }) {
  const [tab, setTab] = useState<Tab>('json');
  const pngRef = useRef<HTMLCanvasElement>(null);

  const cells = editor.cellsRef.current;
  const live: PatternRecord = { ...record, cells, cols: editor.cols, rows: editor.rows, gauge: editor.gauge, palette: record.palette };
  const bundle = makeBundle('pattern', [live]);
  const written = writtenPattern(cells, editor.cols, editor.rows, editor.gauge, editor.yarn);
  const name = slug(record.name);

  useEffect(() => {
    if (open && tab === 'png' && pngRef.current) drawChartPNG(pngRef.current, cells, editor.cols, editor.rows, editor.yarn, 18);
  }, [open, tab, cells, editor.cols, editor.rows, editor.yarn]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Export chart"
      footer={
        tab === 'json' ? (
          <>
            <Button variant="ghost" onClick={() => navigator.clipboard?.writeText(JSON.stringify(bundle, null, 2))}>
              <Copy /> Copy
            </Button>
            <Button variant="primary" onClick={() => save(`${name}.skein.json`, JSON.stringify(bundle, null, 2), 'application/json')}>
              <Download /> Download .skein.json
            </Button>
          </>
        ) : tab === 'written' ? (
          <>
            <Button variant="ghost" onClick={() => navigator.clipboard?.writeText(written)}>
              <Copy /> Copy
            </Button>
            <Button variant="primary" onClick={() => save(`${name}-pattern.txt`, written, 'text/plain')}>
              <Download /> Download .txt
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            onClick={() => {
              const cv = drawChartPNG(document.createElement('canvas'), cells, editor.cols, editor.rows, editor.yarn, 28);
              cv.toBlob((b) => {
                if (!b) return;
                const url = URL.createObjectURL(b);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${name}.png`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              });
            }}
          >
            <Download /> Download .png
          </Button>
        )
      }
    >
      <div className="mb-3">
        <Segmented
          value={tab}
          onChange={setTab}
          options={[
            { value: 'json', label: 'Project file' },
            { value: 'written', label: 'Written pattern' },
            { value: 'png', label: 'Chart image' },
          ]}
        />
      </div>
      {tab === 'json' && (
        <textarea readOnly className="h-64 w-full rounded-xl border border-border bg-surface-2 p-3 font-mono text-xs" value={JSON.stringify(bundle, null, 2)} />
      )}
      {tab === 'written' && (
        <textarea readOnly className="h-64 w-full rounded-xl border border-border bg-surface-2 p-3 font-mono text-xs" value={written} />
      )}
      {tab === 'png' && (
        <div className="flex justify-center overflow-auto rounded-xl border border-border bg-surface-2 p-3">
          <canvas ref={pngRef} className="rounded" style={{ imageRendering: 'pixelated' }} />
        </div>
      )}
    </Modal>
  );
}
